<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-bold text-gray-900">Bookings List</h2>
      <button
        @click="refreshBookings"
        class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Refresh</span>
      </button>
    </div>

    <!-- Filters -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">From Date</label>
        <input
          v-model="filters.dateFrom"
          type="date"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">To Date</label>
        <input
          v-model="filters.dateTo"
          type="date"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select
          v-model="filters.status"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
        >
          <option value="">All Statuses</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Email or reference..."
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      <p class="mt-4 text-gray-600">Loading bookings...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-red-800">{{ error }}</p>
    </div>

    <!-- Bookings Table -->
    <div v-else class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guest</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tour Type</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guests</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="booking in filteredBookings" :key="booking.reference" class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ booking.reference }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ booking.date }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ booking.time }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ booking.firstName }} {{ booking.lastName }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ booking.email }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ booking.tourType }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ booking.guestCount }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ booking.totalPrice }} VT
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <select
                :value="booking.status"
                @change="updateBookingStatus(booking.reference, $event.target.value)"
                :class="[
                  'text-sm px-3 py-1 rounded-full font-medium border-0 cursor-pointer',
                  booking.status === 'paid' ? 'bg-green-100 text-green-800' :
                  booking.status === 'unpaid' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                ]"
              >
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <button
                @click="viewBookingDetails(booking)"
                class="text-blue-600 hover:text-blue-800 mr-3"
              >
                View
              </button>
              <button
                @click="resendEmail(booking.reference)"
                class="text-green-600 hover:text-green-800"
                :disabled="emailSending === booking.reference"
              >
                {{ emailSending === booking.reference ? 'Sending...' : 'Resend' }}
              </button>
            </td>
          </tr>
          <tr v-if="filteredBookings.length === 0">
            <td colspan="10" class="px-6 py-12 text-center text-gray-500">
              No bookings found
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Booking Detail Modal -->
    <div
      v-if="selectedBooking"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="selectedBooking = null"
    >
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <h3 class="text-xl font-bold">Booking Details</h3>
          <button @click="selectedBooking = null" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-500">Reference</p>
            <p class="font-semibold">{{ selectedBooking.reference }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Status</p>
            <span
              :class="[
                'inline-block px-3 py-1 rounded-full text-sm font-medium',
                selectedBooking.status === 'paid' ? 'bg-green-100 text-green-800' :
                selectedBooking.status === 'unpaid' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              ]"
            >
              {{ selectedBooking.status }}
            </span>
          </div>
          <div>
            <p class="text-sm text-gray-500">Guest Name</p>
            <p class="font-semibold">{{ selectedBooking.firstName }} {{ selectedBooking.lastName }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Email</p>
            <p class="font-semibold">{{ selectedBooking.email }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Date</p>
            <p class="font-semibold">{{ selectedBooking.date }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Time</p>
            <p class="font-semibold">{{ selectedBooking.time }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Tour Type</p>
            <p class="font-semibold">{{ selectedBooking.tourType }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Number of Guests</p>
            <p class="font-semibold">{{ selectedBooking.guestCount }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Language</p>
            <p class="font-semibold">{{ selectedBooking.preferredLanguage }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Total Price</p>
            <p class="font-semibold">{{ selectedBooking.totalPrice }} VT</p>
          </div>
          <div v-if="selectedBooking.createdAt" class="col-span-2">
            <p class="text-sm text-gray-500">Created At</p>
            <p class="font-semibold">{{ new Date(selectedBooking.createdAt).toLocaleString() }}</p>
          </div>
        </div>

        <div class="mt-6 pt-6 border-t flex justify-end">
          <button
            @click="selectedBooking = null"
            class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { format, subMonths } from 'date-fns'
import { useAuth } from '../../composables/useAuth'

const { getAuthAxios } = useAuth()
const bookings = ref([])
const loading = ref(false)
const error = ref(null)
const selectedBooking = ref(null)
const emailSending = ref(null)

const filters = ref({
  dateFrom: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
  dateTo: format(new Date(), 'yyyy-MM-dd'),
  status: '',
  search: ''
})

const fetchBookings = async () => {
  loading.value = true
  error.value = null

  try {
    const authAxios = getAuthAxios()
    const params = {
      limit: 500,
      sortBy: 'date',
      sortOrder: 'desc'
    }

    if (filters.value.dateFrom) params.dateFrom = filters.value.dateFrom
    if (filters.value.dateTo) params.dateTo = filters.value.dateTo
    if (filters.value.status) params.status = filters.value.status

    const response = await authAxios.get('/api/admin-bookings', { params })

    if (response.data.success) {
      bookings.value = response.data.bookings
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load bookings'
    console.error('Error fetching bookings:', err)
  } finally {
    loading.value = false
  }
}

const filteredBookings = computed(() => {
  if (!filters.value.search) return bookings.value

  const search = filters.value.search.toLowerCase()
  return bookings.value.filter(booking =>
    booking.email.toLowerCase().includes(search) ||
    booking.reference.toLowerCase().includes(search) ||
    `${booking.firstName} ${booking.lastName}`.toLowerCase().includes(search)
  )
})

const updateBookingStatus = async (reference, newStatus) => {
  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.patch(`/api/admin-bookings?reference=${reference}`, {
      status: newStatus
    })

    if (response.data.success) {
      // Update local booking
      const booking = bookings.value.find(b => b.reference === reference)
      if (booking) {
        booking.status = newStatus
      }
    }
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to update booking status')
    console.error('Error updating booking:', err)
  }
}

const viewBookingDetails = (booking) => {
  selectedBooking.value = booking
}

const resendEmail = async (reference) => {
  if (!confirm('Resend confirmation email to customer?')) return

  emailSending.value = reference

  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.post(`/api/admin-bookings?action=resend-email&reference=${reference}`)

    if (response.data.success) {
      alert('Email sent successfully!')
    }
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to send email')
    console.error('Error sending email:', err)
  } finally {
    emailSending.value = null
  }
}

const refreshBookings = () => {
  fetchBookings()
}

watch(
  () => [filters.value.dateFrom, filters.value.dateTo, filters.value.status],
  () => {
    fetchBookings()
  },
  { deep: true }
)

onMounted(() => {
  fetchBookings()
})
</script>

<style scoped>
.text-gold {
  color: #F6D993;
}
</style>
